import crypto from 'node:crypto'
import fs from 'node:fs'
import path from 'node:path'
import type { FormSchema, GeneratedFile, Schema } from '@core-engine/schema/types.js'
import { stringifyProjectJson } from '@core-engine/utils/json-format.js'

export interface SnapshotManifestFile {
  path: string
  hash: string
}

export interface SnapshotManifest {
  schemaName: string
  schemaHash: string
  schemaJson: Schema | FormSchema | { name: string }
  cliVersion: string
  generatedAt: string
  files: SnapshotManifestFile[]
  skipped: string[]
}

export const SNAPSHOTS_DIR = '.betterstart/snapshots'
export const BARREL_SCOPE = '_barrels'
export const REMOVED_SCOPE = '_removed'

export function normalizeLineEndings(content: string): string {
  return content.replace(/\r\n/g, '\n')
}

export function hashContent(content: string): string {
  return `sha256-${crypto.createHash('sha256').update(normalizeLineEndings(content)).digest('hex')}`
}

function snapshotRoot(cwd: string): string {
  return path.join(cwd, '.betterstart', 'snapshots')
}

export function scopeDir(cwd: string, scope: string): string {
  return path.join(snapshotRoot(cwd), scope)
}

export function manifestPath(cwd: string, scope: string): string {
  return path.join(scopeDir(cwd, scope), 'manifest.json')
}

export function filesDir(cwd: string, scope: string): string {
  return path.join(scopeDir(cwd, scope), 'files')
}

function snapshotFilePath(cwd: string, scope: string, filePath: string): string {
  return path.join(filesDir(cwd, scope), ...filePath.split('/'))
}

export function removedDir(cwd: string): string {
  return path.join(snapshotRoot(cwd), REMOVED_SCOPE)
}

export function tombstonePath(cwd: string, scope: string): string {
  return path.join(removedDir(cwd), scope)
}

export function loadManifest(cwd: string, scope: string): SnapshotManifest | null {
  const filePath = manifestPath(cwd, scope)
  if (!fs.existsSync(filePath)) {
    return null
  }

  return JSON.parse(fs.readFileSync(filePath, 'utf-8')) as SnapshotManifest
}

export function saveManifest(cwd: string, scope: string, manifest: SnapshotManifest): void {
  fs.mkdirSync(scopeDir(cwd, scope), { recursive: true })
  fs.writeFileSync(manifestPath(cwd, scope), stringifyProjectJson(manifest), 'utf-8')
}

export function writeSnapshotFiles(
  cwd: string,
  scope: string,
  files: GeneratedFile[]
): SnapshotManifestFile[] {
  const dir = filesDir(cwd, scope)
  fs.rmSync(dir, { recursive: true, force: true })
  fs.mkdirSync(dir, { recursive: true })

  return files.map((file) => {
    const outputPath = snapshotFilePath(cwd, scope, file.path)
    fs.mkdirSync(path.dirname(outputPath), { recursive: true })
    fs.writeFileSync(outputPath, file.content, 'utf-8')
    return {
      path: file.path,
      hash: hashContent(file.content)
    }
  })
}

export function readSnapshotFiles(
  cwd: string,
  scope: string,
  manifest: SnapshotManifest
): GeneratedFile[] {
  return manifest.files
    .filter((entry) => fs.existsSync(snapshotFilePath(cwd, scope, entry.path)))
    .map((entry) => ({
      path: entry.path,
      content: fs.readFileSync(snapshotFilePath(cwd, scope, entry.path), 'utf-8')
    }))
}

export function deleteSnapshot(cwd: string, scope: string): void {
  fs.rmSync(scopeDir(cwd, scope), { recursive: true, force: true })
}

export function hasSnapshotFiles(cwd: string, scope: string): boolean {
  return fs.existsSync(filesDir(cwd, scope))
}

export function writeTombstone(cwd: string, scope: string): void {
  fs.mkdirSync(removedDir(cwd), { recursive: true })
  fs.writeFileSync(tombstonePath(cwd, scope), '', 'utf-8')
}

export function hasTombstone(cwd: string, scope: string): boolean {
  return fs.existsSync(tombstonePath(cwd, scope))
}

export function clearTombstone(cwd: string, scope: string): void {
  fs.rmSync(tombstonePath(cwd, scope), { force: true })
}

export function listTombstones(cwd: string): string[] {
  const dir = removedDir(cwd)
  if (!fs.existsSync(dir)) {
    return []
  }

  return fs.readdirSync(dir).sort()
}

export function listSnapshotScopes(cwd: string): string[] {
  const dir = snapshotRoot(cwd)
  if (!fs.existsSync(dir)) {
    return []
  }

  return fs
    .readdirSync(dir, { withFileTypes: true })
    .filter((entry) => entry.isDirectory())
    .map((entry) => entry.name)
    .filter((name) => name !== REMOVED_SCOPE)
    .sort()
}

export function createManifest(
  scope: string,
  schemaJson: Schema | FormSchema | { name: string },
  cliVersion: string,
  files: GeneratedFile[],
  skipped: string[]
): SnapshotManifest {
  return {
    schemaName: scope,
    schemaHash: hashContent(JSON.stringify(schemaJson)),
    schemaJson,
    cliVersion,
    generatedAt: new Date().toISOString(),
    files: files.map((file) => ({
      path: file.path,
      hash: hashContent(file.content)
    })),
    skipped: [...skipped].sort()
  }
}

export function scopeFromSchema(schema: Schema | FormSchema): string {
  return schema.name
}

export function snapshotRootExists(cwd: string): boolean {
  return fs.existsSync(snapshotRoot(cwd))
}

export function manifestFilesByPath(manifest: SnapshotManifest): Map<string, SnapshotManifestFile> {
  return new Map(manifest.files.map((file) => [file.path, file]))
}

export function loadAllManifests(
  cwd: string
): Array<{ scope: string; manifest: SnapshotManifest }> {
  return listSnapshotScopes(cwd)
    .filter((scope) => scope !== BARREL_SCOPE)
    .map((scope) => ({ scope, manifest: loadManifest(cwd, scope) }))
    .filter(
      (entry): entry is { scope: string; manifest: SnapshotManifest } => entry.manifest !== null
    )
}

export function snapshotTargetPath(cwd: string, filePath: string): string {
  return path.join(cwd, ...filePath.split('/'))
}

export function getCurrentCliVersion(): string {
  let currentDir = path.dirname(new URL(import.meta.url).pathname)

  for (let depth = 0; depth < 6; depth++) {
    const packagePath = path.join(currentDir, 'package.json')
    if (fs.existsSync(packagePath)) {
      try {
        const pkg = JSON.parse(fs.readFileSync(packagePath, 'utf-8')) as {
          name?: string
          version?: string
        }

        if (pkg.name === 'betterstart-cli' && pkg.version) {
          return pkg.version
        }
      } catch {
        // Keep walking upward.
      }
    }

    const parentDir = path.dirname(currentDir)
    if (parentDir === currentDir) {
      break
    }
    currentDir = parentDir
  }

  return '0.0.0'
}

export function getAllTrackedFilePaths(cwd: string): string[] {
  const paths = new Set<string>()

  for (const { manifest } of loadAllManifests(cwd)) {
    for (const file of manifest.files) {
      paths.add(file.path)
    }
  }

  const barrelManifest = loadManifest(cwd, BARREL_SCOPE)
  if (barrelManifest) {
    for (const file of barrelManifest.files) {
      paths.add(file.path)
    }
  }

  return Array.from(paths).sort()
}
